import { useCallback, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from './client';
import { FlagResponse, CoinAdjustResponse, RefundResponse } from './types';

/**
 * Mutations against a single user: flag / unflag (SSA-811), coin adjustment, and
 * refunding a confirmed redemption. `reason` minimums are enforced server-side
 * (flag >= 5 chars, adjust and refund >= 20) but the panel validates first for a
 * friendlier error.
 */
export function useUserActions(env: Env) {
  const { request } = useAdminApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const flagUser = useCallback(
    async (userId: string, reason: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await request<FlagResponse>({
          env,
          path: `/admin/gamification/users/${userId}/flag`,
          method: 'POST',
          body: { reason },
        });
        return true;
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Flag failed');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [request, env],
  );

  const unflagUser = useCallback(
    async (userId: string, reason?: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await request<FlagResponse>({
          env,
          path: `/admin/gamification/users/${userId}/flag`,
          method: 'DELETE',
          body: reason ? { reason } : undefined,
        });
        return true;
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Unflag failed');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [request, env],
  );

  const adjustCoins = useCallback(
    async (
      userId: string,
      amount: number,
      reasonCode: string,
      reason: string,
      idempotencyKey: string,
    ): Promise<CoinAdjustResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        return await request<CoinAdjustResponse>({
          env,
          path: `/admin/gamification/users/${userId}/adjust`,
          method: 'POST',
          body: { amount, reason_code: reasonCode, reason, idempotency_key: idempotencyKey },
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Adjustment failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [request, env],
  );

  /**
   * Refund a confirmed redemption: credits the coins back and flips it to
   * `refunded`. The endpoint is idempotent — a retry (or a second admin) gets
   * `already_refunded: true` and no second credit.
   */
  const refundRedemption = useCallback(
    async (redemptionId: string, reason: string): Promise<RefundResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        return await request<RefundResponse>({
          env,
          path: `/admin/gamification/redemptions/${redemptionId}/refund`,
          method: 'POST',
          body: { reason },
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Refund failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [request, env],
  );

  return { loading, error, flagUser, unflagUser, adjustCoins, refundRedemption, setError };
}
